/**
 * document-upload.component.ts
 * ----------------------------
 * Module 4 - Document Upload page.
 *
 * Supports two modes (isEditMode flag):
 *
 *  NEW  (isEditMode = false):
 *    Standard first-visit. POST /api/v1/sessions/upload creates a new session.
 *
 *  EDIT (isEditMode = true - user navigated back from a later step):
 *    - On init: GET /api/v1/sessions/{id} pre-populates all fields and shows
 *      already-uploaded files alongside any new files queued for upload.
 *    - User can edit fields, mark existing files for deletion, add new files.
 *    - On submit: PATCH /api/v1/sessions/{id} applies all changes atomically.
 *
 * Route guard: stepGuard('document-upload') - requires flow mode = 'new'.
 */

import { Component, OnInit, HostListener, OnDestroy } from '@angular/core';
import { CommonModule }                     from '@angular/common';
import { FormsModule }                      from '@angular/forms';
import { Router }                           from '@angular/router';
import { MsalService }                      from '@azure/msal-angular';

import { UploadService, ApplicationDetailsPayload, UploadedFileInfo }
  from '../../core/services/upload.service';
import { SessionService }
  from '../../core/services/session.service';

interface AppTypeOption { value: string; label: string; }

interface UploadForm {
  persona:         string;
  analystName:     string;
  catId:           string;
  appId:           string;
  appName:         string;
  managementStyle: string;
  appTypes:        string[];
  tier:            string;
  slLevel:         string;
  archDescription: string;
  additionalInfo:  string;
  linkCount:       number;
  links:           LinkEntry[];
}

interface LinkEntry { value: string; }

/**
 * source='new'      : freshly added by the user; has a File object.
 * source='existing' : already stored in the backend; file is null.
 * markedForDeletion : pending removal on next PATCH (existing only).
 */
interface DocumentEntry {
  file:              File | null;
  displayName:       string;
  displaySize:       number;
  status:            'pending' | 'uploaded';
  source:            'new' | 'existing';
  storedFilename:    string | null;
  markedForDeletion: boolean;
}

@Component({
  selector:    'app-document-upload',
  standalone:  true,
  imports:     [CommonModule, FormsModule],
  templateUrl: './document-upload.component.html',
  styleUrl:    './document-upload.component.scss',
})
export class DocumentUploadComponent implements OnInit, OnDestroy {

  constructor(
    private readonly authService:    MsalService,
    private readonly uploadService:  UploadService,
    private readonly sessionService: SessionService,
    private readonly router:         Router,
  ) {}

  // Application Type options
  readonly appTypeOptions: AppTypeOption[] = [
    { value: 'IT',   label: 'Information Technology (IT)' },
    { value: 'OT',   label: 'Operational Technology (OT)' },
    { value: 'IIOT', label: 'Industrial IoT (IIOT)'       },
  ];
  appTypeDropdownOpen = false;

  // Form model
  form: UploadForm = {
    persona:         '',
    analystName:     '',
    catId:           '',
    appId:           '',
    appName:         '',
    managementStyle: '',
    appTypes:        [],
    tier:            '',
    slLevel:         '',
    archDescription: '',
    additionalInfo:  '',
    linkCount:       0,
    links:           [],
  };

  // File state
  archFile:      File | null     = null;
  // Holds a file that cannot be used as the architecture diagram — either an
  // unsupported type or one over the size limit. It is never uploaded; kept
  // only so the UI can show a removable chip prompting the user to fix it.
  archFileRejected:       File | null              = null;
  archFileRejectedReason: 'type' | 'size' | null   = null;
  artifactFiles: DocumentEntry[] = [];

  // Allowed MIME types for the architecture diagram field
  private readonly ARCH_ALLOWED_TYPES = new Set([
    'application/pdf',
    'image/png', 'image/jpeg', 'image/bmp', 'image/tiff', 'image/gif', 'image/webp',
  ]);
  private readonly ARCH_ALLOWED_EXTS = new Set([
    '.pdf', '.png', '.jpg', '.jpeg', '.bmp', '.tiff', '.tif', '.gif', '.webp',
  ]);

  // Human-readable list of accepted architecture-diagram formats, shown in
  // the dropzone hint and the unsupported-file message.
  readonly ARCH_ALLOWED_LABEL = 'PDF, PNG, JPG, JPEG, BMP, TIFF';

  // Allowed extensions for artifact files.
  // Mirrors the backend ALLOWED_MIME_TYPES allow-list (file_utils.py) so that
  // unsupported files are rejected on the client before they are ever uploaded.
  private readonly ARTIFACT_ALLOWED_EXTS = new Set([
    '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
    '.txt', '.csv', '.png', '.jpg', '.jpeg', '.gif', '.bmp', '.tif', '.tiff',
  ]);

  // Human-readable list of accepted formats, shown in validation messages.
  readonly ARTIFACT_ALLOWED_LABEL =
    'PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, CSV, PNG, JPG, JPEG, GIF, BMP, TIFF';

  // Per-file size limit — mirrors the backend max_file_size_bytes (25 MB).
  private readonly MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;
  readonly MAX_FILE_SIZE_LABEL = '25 MB';

  // Maximum number of files per session (architecture diagram + artifacts) —
  // mirrors the backend max_files_per_session (50).
  readonly MAX_FILES = 50;

  // Drag-over state
  archDragOver      = false;
  artifactsDragOver = false;

  // Link protection
  linkReduceWarning = false;

  // Submission / loading state
  isSubmitting     = false;
  isLoadingSession = false;
  submitError:     string | null = null;

  // Clear All confirmation modal
  showClearConfirm = false;

  // Non-ASCII filename warning
  nonAsciiFiles: string[] = [];

  // Unsupported file-type warning (artifact files whose extension is not on
  // the backend allow-list). Blocks submission until the user removes them.
  unsupportedFiles: string[] = [];

  // Oversized-file warning (files exceeding the per-file size limit).
  // Blocks submission until the user removes them.
  oversizedFiles: string[] = [];

  // Success toast
  showSuccessToast = false;
  successMessage   = '';
  private _toastTimer: ReturnType<typeof setTimeout> | null = null;

  // Edit mode: true when user navigates back to this page mid-flow
  isEditMode = false;

  /**
   * Snapshot of the form + file list taken immediately after a session is
   * loaded in edit mode.  Used to detect whether the user made any changes
   * before clicking "Upload & Continue" — if nothing changed we skip the
   * expensive re-upload and navigate directly to /category-review.
   */
  private _loadedSnapshot = '';

  // Tracks which fields the user has blurred (for inline validation)
  touchedFields: Record<string, boolean> = {};

  // ---- Lifecycle ----------------------------------------------------------

  ngOnInit(): void {    const account = this.authService.instance.getActiveAccount()
      ?? this.authService.instance.getAllAccounts()[0]
      ?? null;
    this.form.analystName = account?.name ?? account?.username ?? '';
    this.syncLinksArray();

    // Feature #3: if a session already exists in this tab the user is navigating
    // back - pre-populate the form from the backend metadata.
    const existingSessionId = this.sessionService.sessionId();
    if (existingSessionId) {
      this.isEditMode = true;
      this._loadExistingSession(existingSessionId);
    }
  }

  ngOnDestroy(): void {
    if (this._toastTimer !== null) { clearTimeout(this._toastTimer); }
  }

  /**
   * Show "Leave site?" dialog when the user closes the tab / refreshes,
   * but only when the form has been modified and not yet submitted.
   */
  @HostListener('window:beforeunload', ['$event'])
  onWindowBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.isFormDirty && !this.isSubmitting) {
      event.preventDefault();
      event.returnValue = ''; // required for Chrome / Edge
    }
  }

  // ---- Computed -----------------------------------------------------------

  get isOtSelected(): boolean { return this.form.appTypes.includes('OT'); }

  /** True once the user has touched / changed any form field. */
  get isFormDirty(): boolean {
    const f = this.form;
    return !!(f.catId || f.appId || f.appName || f.managementStyle ||
              f.appTypes.length || f.tier ||
              f.archDescription.trim() || this.archFile || this.artifactFiles.length);
  }

  get isFormReady(): boolean {
    const f = this.form;
    const hasBaseFields = !!f.analystName && !!f.appName &&
                          !!f.appId     && !!f.tier    &&
                          !!f.managementStyle;
    const hasAppType = f.appTypes.length > 0;
    const hasArch    = !!this.archFile || f.archDescription.trim().length > 0
                       || this.artifactFiles.some(e => e.source === 'existing' && !e.markedForDeletion);
    return hasBaseFields && hasAppType && hasArch
           && !this.isSubmitting && !this.isLoadingSession
           && !this.hasNonAsciiFiles && !this.hasUnsupportedFiles
           && !this.hasOversizedFiles && !this.hasTooManyFiles
           && !this.hasInvalidLinks && !this.hasInvalidCatId;
  }

  /**
   * Returns a human-readable list of reasons why the submit button is
   * currently disabled.  Used to render the inline hint panel.
   */
  get disabledReasons(): string[] {
    const f = this.form;
    const items: string[] = [];
    if (!f.analystName)       { items.push('Analyst/Engineer Name is missing'); }
    if (!f.appId)             { items.push('Application ID is required'); }
    if (this.hasInvalidCatId) { items.push('CAT ID must contain numbers only, either leave it blank or enter digits'); }
    if (!f.appName.trim())    { items.push('Application Name is required'); }
    if (!f.managementStyle)   { items.push('Management Style must be selected'); }
    if (!f.tier)              { items.push('Tier must be selected'); }
    if (f.appTypes.length === 0) { items.push('At least one Application Type must be selected'); }
    const hasArch = !!this.archFile || f.archDescription.trim().length > 0
                    || this.artifactFiles.some(e => e.source === 'existing' && !e.markedForDeletion);
    if (!hasArch) { items.push('Architecture Diagram or Description is required'); }
    if (this.hasNonAsciiFiles) { items.push('Rename files with non-ASCII characters before uploading'); }
    if (this.hasUnsupportedFiles) { items.push('Remove unsupported file type(s) — only ' + this.ARTIFACT_ALLOWED_LABEL + ' are accepted'); }
    if (this.hasOversizedFiles) { items.push('Remove file(s) larger than ' + this.MAX_FILE_SIZE_LABEL + ' — each file must be ' + this.MAX_FILE_SIZE_LABEL + ' or smaller'); }
    if (this.hasTooManyFiles) { items.push('Too many files — a maximum of ' + this.MAX_FILES + ' files are allowed (you have ' + this.totalFileCount + '; remove at least ' + (this.totalFileCount - this.MAX_FILES) + ')'); }
    if (this.hasInvalidLinks) { items.push('Fix the webpage link(s) — only https:// addresses to a website domain are allowed (no http:// and no IP addresses such as https://0.0.0.0/8000)'); }
    return items;
  }

  /** True when any queued/selected file has a non-ASCII character in its name. */
  get hasNonAsciiFiles(): boolean {
    return this.nonAsciiFiles.length > 0;
  }

  /** True when any newly-selected artifact file has an unsupported extension. */
  get hasUnsupportedFiles(): boolean {
    return this.unsupportedFiles.length > 0;
  }

  /** True when any newly-selected file exceeds the per-file size limit. */
  get hasOversizedFiles(): boolean {
    return this.oversizedFiles.length > 0;
  }

  /**
   * Total number of files that will be uploaded to the session: the new
   * architecture diagram (if any) plus every non-deleted artifact. Mirrors
   * the file list the backend counts against max_files_per_session.
   */
  get totalFileCount(): number {
    return (this.archFile ? 1 : 0) + this.visibleArtifacts.length;
  }

  /** True when the attached file count exceeds the per-session maximum. */
  get hasTooManyFiles(): boolean {
    return this.totalFileCount > this.MAX_FILES;
  }

  /**
   * Validates a single webpage URL the same way the backend does:
   *   - must be https:// (http:// and every other scheme is rejected)
   *   - must point at a real domain name (www.example.com)
   *   - IP-address URLs (e.g. https://0.0.0.0/8000), localhost and URLs with
   *     embedded credentials are rejected.
   * An empty value is treated as valid (blank link rows are dropped on submit).
   */
  isValidWebpageUrl(raw: string): boolean {
    const value = (raw || '').trim();
    if (!value) { return true; }

    let url: URL;
    try {
      url = new URL(value);
    } catch {
      return false;
    }

    // https only — reject http:// and all other schemes.
    if (url.protocol !== 'https:') { return false; }
    // No embedded credentials (https://user:pass@host).
    if (url.username || url.password) { return false; }

    const host = url.hostname.toLowerCase();
    if (!host) { return false; }
    // Reject localhost and IPv6 literals (hostname keeps ':' for IPv6).
    if (host === 'localhost' || host.endsWith('.localhost')) { return false; }
    if (host.includes(':') || value.includes('[')) { return false; }
    // Reject IPv4 literals such as 0.0.0.0 / 127.0.0.1.
    if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) { return false; }
    // Must be a dotted domain name ending in an alphabetic TLD.
    if (!/^[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}$/.test(host)) { return false; }
    return true;
  }

  /** Per-row error message for a webpage link (null when valid/empty). */
  linkError(value: string): string | null {
    if (this.isValidWebpageUrl(value)) { return null; }
    return 'Enter a valid https:// website URL (e.g. https://www.example.com). ' +
           'http:// and IP addresses such as https://0.0.0.0/8000 are not allowed.';
  }

  /** The non-empty link values that fail validation. */
  get invalidLinks(): string[] {
    return this.form.links
      .map(l => l.value.trim())
      .filter(v => v.length > 0 && !this.isValidWebpageUrl(v));
  }

  /** True when any filled-in webpage link is invalid. Blocks submission. */
  get hasInvalidLinks(): boolean {
    return this.invalidLinks.length > 0;
  }

  /**
   * Returns true if the given filename contains characters outside the
   * printable ASCII range (code-points 32–126).
   */
  private _hasNonAscii(filename: string): boolean {
    // eslint-disable-next-line no-control-regex
    return /[^\x20-\x7E]/.test(filename);
  }

  /**
   * Returns true if the filename's extension is on the artifact allow-list.
   * Files with no extension are treated as unsupported (the backend cannot
   * reliably detect their MIME type and will skip them).
   */
  private _isAllowedArtifact(filename: string): boolean {
    const dot = filename.lastIndexOf('.');
    if (dot === -1) { return false; }
    return this.ARTIFACT_ALLOWED_EXTS.has(filename.slice(dot).toLowerCase());
  }

  // ── Inline validation error getters (shown after the field is blurred) ──

  /**
   * True when CAT ID is filled but contains anything other than digits.
   * CAT ID is optional, so blank is valid — but if provided it must be
   * numbers only. Used to block submission and show a message.
   */
  get hasInvalidCatId(): boolean {
    return !!this.form.catId && !/^\d+$/.test(this.form.catId);
  }

  get catIdError(): string | null {
    if (!this.touchedFields['catId']) { return null; }
    if (this.hasInvalidCatId) {
      return 'CAT ID must contain numbers only, either leave it blank or enter digits.';
    }
    return null;
  }

  get appIdError(): string | null {
    if (!this.touchedFields['appId']) { return null; }
    if (!this.form.appId)             { return 'Application ID is required as integer.'; }
    return null;
  }

  /**
   * True when the entered Application ID consists entirely of zeros
   * (e.g. "0", "00", "000"). In that case the report won't be
   * retrievable by App ID later, but submission is still allowed.
   */
  get isAppIdZero(): boolean {
    const val = this.form.appId?.trim();
    if (!val) { return false; }
    return /^0+$/.test(val);
  }

  get appNameError(): string | null {
    if (!this.touchedFields['appName']) { return null; }
    if (!this.form.appName.trim())      { return 'Application Name is required.'; }
    return null;
  }

  get archError(): string | null {
    if (!this.touchedFields['archDescription']) { return null; }
    const hasArch = !!this.archFile || this.form.archDescription.trim().length > 0
                    || this.artifactFiles.some(e => e.source === 'existing' && !e.markedForDeletion);
    if (!hasArch) { return 'Please upload a diagram or provide a description.'; }
    return null;
  }

  get appTypeLabel(): string {
    if (this.form.appTypes.length === 0) { return 'Select Type(s)'; }
    return this.form.appTypes
      .map(v => this.appTypeOptions.find(o => o.value === v)?.label ?? v)
      .join(', ');
  }

  /** All non-deleted entries shown in the document list. */
  get visibleArtifacts(): DocumentEntry[] {
    return this.artifactFiles.filter(e => !e.markedForDeletion);
  }

  /** New files not yet submitted (source='new'). Used in the dropzone chip area. */
  get newArtifacts(): DocumentEntry[] {
    return this.artifactFiles.filter(e => e.source === 'new');
  }

  /** Count of existing files marked for deletion. Used in the warning banner. */
  get pendingDeletionCount(): number {
    return this.artifactFiles.filter(e => e.markedForDeletion).length;
  }

  // ---- Dropdown toggles ---------------------------------------------------

  /** Mark a field as touched so its validation error becomes visible. */
  onFieldBlur(field: string): void {
    this.touchedFields[field] = true;
  }

  onAppTypeToggle(value: string): void {
    const idx = this.form.appTypes.indexOf(value);
    if (idx === -1) { this.form.appTypes = [...this.form.appTypes, value]; }
    else            { this.form.appTypes = this.form.appTypes.filter(v => v !== value); }
    if (!this.isOtSelected) { this.form.slLevel = ''; }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.checkbox-dropdown')) {
      this.appTypeDropdownOpen = false;
    }
  }

  // ---- Link helpers -------------------------------------------------------

  onLinkCountChange(newCount: number): void {
    const currentCount = this.form.linkCount;
    if (newCount < currentCount) {
      const filledBelow = this.form.links.slice(newCount).some(l => l.value.trim() !== '');
      if (filledBelow) {
        this.linkReduceWarning = true;
        setTimeout(() => { this.form.linkCount = currentCount; }, 0);
        setTimeout(() => { this.linkReduceWarning = false; }, 3500);
        return;
      }
    }
    this.linkReduceWarning = false;
    this.form.linkCount = newCount;
    this.syncLinksArray();
  }

  private syncLinksArray(): void {
    while (this.form.links.length < this.form.linkCount) { this.form.links.push({ value: '' }); }
    while (this.form.links.length > this.form.linkCount) { this.form.links.pop(); }
  }

  trackByIndex(index: number): number { return index; }

  // ---- File handlers ------------------------------------------------------

  onArchFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.setArchFile(input.files[0]);
      // Reset so the same file can be re-selected after removal
      input.value = '';
    }
  }

  removeArchFile(): void {
    this.archFile = null;
    this.archFileRejected = null;
    this.archFileRejectedReason = null;
    this._refreshNonAsciiCheck();
  }

  /**
   * Validate and set the architecture-diagram file.
   *
   * A file is rejected (kept as a removable chip, never uploaded) when its
   * type is not allowed OR it exceeds the per-file size limit. `archFileRejectedReason`
   * distinguishes the two so the UI can show the right tag and message.
   */
  private setArchFile(file: File): void {
    const ext = '.' + file.name.split('.').pop()!.toLowerCase();
    if (!this.ARCH_ALLOWED_TYPES.has(file.type) && !this.ARCH_ALLOWED_EXTS.has(ext)) {
      this.archFileRejected = file;
      this.archFileRejectedReason = 'type';
      this.archFile = null;
      this._refreshNonAsciiCheck();
      return;
    }
    if (file.size > this.MAX_FILE_SIZE_BYTES) {
      this.archFileRejected = file;
      this.archFileRejectedReason = 'size';
      this.archFile = null;
      this._refreshNonAsciiCheck();
      return;
    }
    this.archFileRejected = null;
    this.archFileRejectedReason = null;
    this.archFile = file;
    this._refreshNonAsciiCheck();
  }

  onArtifactsSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const newEntries: DocumentEntry[] = Array.from(input.files).map(file => ({
        file,
        displayName:       file.name,
        displaySize:       file.size,
        status:            'pending' as const,
        source:            'new' as const,
        storedFilename:    null,
        markedForDeletion: false,
      }));
      this.artifactFiles = [...this.artifactFiles, ...newEntries];
      this._refreshNonAsciiCheck();
    }
  }

  /**
   * Remove / mark-for-deletion an artifact entry.
   * NEW files are spliced out immediately.
   * EXISTING files (backend) are marked for deletion; a strikethrough is shown
   * and a Restore button appears so the user can undo before saving.
   */
  removeArtifact(index: number): void {
    const entry = this.artifactFiles[index];
    if (!entry) { return; }
    if (entry.source === 'new') {
      this.artifactFiles.splice(index, 1);
    } else {
      this.artifactFiles[index] = { ...entry, markedForDeletion: true };
    }
    this._refreshNonAsciiCheck();
  }

  /** Undo a pending deletion of an existing backend file. */
  restoreArtifact(index: number): void {
    const entry = this.artifactFiles[index];
    if (entry?.source === 'existing') {
      this.artifactFiles[index] = { ...entry, markedForDeletion: false };
    }
  }

  onDragOver(event: DragEvent, zone: 'arch' | 'artifacts'): void {
    event.preventDefault();
    if (zone === 'arch')      { this.archDragOver      = true; }
    if (zone === 'artifacts') { this.artifactsDragOver = true; }
  }

  onDragLeave(zone: 'arch' | 'artifacts'): void {
    if (zone === 'arch')      { this.archDragOver      = false; }
    if (zone === 'artifacts') { this.artifactsDragOver = false; }
  }

  onDrop(event: DragEvent, zone: 'arch' | 'artifacts'): void {
    event.preventDefault();
    const files = event.dataTransfer?.files;
    if (!files) { return; }
    if (zone === 'arch') {
      this.archDragOver = false;
      if (files[0]) { this.setArchFile(files[0]); }
    }
    if (zone === 'artifacts') {
      this.artifactsDragOver = false;
      const newEntries: DocumentEntry[] = Array.from(files).map(file => ({
        file,
        displayName:       file.name,
        displaySize:       file.size,
        status:            'pending' as const,
        source:            'new' as const,
        storedFilename:    null,
        markedForDeletion: false,
      }));
      this.artifactFiles = [...this.artifactFiles, ...newEntries];
      this._refreshNonAsciiCheck();
    }
  }

  // ---- Sanitisation -------------------------------------------------------

  sanitize(value: string): string {
    return value.replace(/[<>"'`;]/g, '').trimStart();
  }

  sanitizeNumeric(value: string, maxLen: number): string {
    return value.replace(/\D/g, '').slice(0, maxLen);
  }

  /**
   * Rebuild the file-validation warning lists from all currently selected
   * files. Covers the non-ASCII filename check, the unsupported file-type
   * check, and the per-file size check. Runs whenever a file is added or
   * removed (browse + drop).
   */
  private _refreshNonAsciiCheck(): void {
    const offenders: string[] = [];
    const unsupported: string[] = [];
    const oversized: string[] = [];

    // The architecture diagram is validated for type + size in setArchFile(),
    // so a set archFile is always valid here — only its name needs checking.
    if (this.archFile && this._hasNonAscii(this.archFile.name)) {
      offenders.push(this.archFile.name);
    }
    for (const entry of this.artifactFiles) {
      if (entry.source !== 'new' || entry.markedForDeletion) { continue; }
      if (this._hasNonAscii(entry.displayName)) {
        offenders.push(entry.displayName);
      }
      if (!this._isAllowedArtifact(entry.displayName)) {
        unsupported.push(entry.displayName);
      }
      if (entry.displaySize > this.MAX_FILE_SIZE_BYTES) {
        oversized.push(entry.displayName);
      }
    }
    this.nonAsciiFiles    = offenders;
    this.unsupportedFiles = unsupported;
    this.oversizedFiles   = oversized;
  }

  // ---- Display helpers ----------------------------------------------------

  getFileSize(bytes: number): string {
    if (bytes < 1024)        { return `${bytes} B`; }
    if (bytes < 1024 * 1024) { return `${(bytes / 1024).toFixed(1)} KB`; }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  /**
   * True when a document tile represents a newly-added artifact with an
   * unsupported file type. Used to flag the offending file in the list.
   */
  isUnsupportedEntry(entry: DocumentEntry): boolean {
    return entry.source === 'new'
        && !entry.markedForDeletion
        && !this._isAllowedArtifact(entry.displayName);
  }

  /**
   * True when a document tile represents a newly-added artifact that exceeds
   * the per-file size limit. Used to flag the offending file in the list.
   */
  isOversizedEntry(entry: DocumentEntry): boolean {
    return entry.source === 'new'
        && !entry.markedForDeletion
        && entry.displaySize > this.MAX_FILE_SIZE_BYTES;
  }

  /**
   * True when a document tile represents a newly-added artifact whose file
   * name contains non-ASCII characters (not allowed for blob uploads). Used
   * to flag the offending file in the list.
   */
  isNonAsciiEntry(entry: DocumentEntry): boolean {
    return entry.source === 'new'
        && !entry.markedForDeletion
        && this._hasNonAscii(entry.displayName);
  }

  /**
   * True when the selected architecture diagram's file name contains
   * non-ASCII characters. The arch file is shown as a chip (not a list tile),
   * so it needs its own flag for the inline non-ASCII indicator.
   */
  get isArchFileNonAscii(): boolean {
    return !!this.archFile && this._hasNonAscii(this.archFile.name);
  }

  /** Show a success toast for `durationMs` milliseconds then auto-dismiss. */
  private _showToast(message: string, durationMs = 4000): void {
    if (this._toastTimer !== null) { clearTimeout(this._toastTimer); }
    this.successMessage   = message;
    this.showSuccessToast = true;
    this._toastTimer = setTimeout(() => {
      this.showSuccessToast = false;
      this._toastTimer = null;
    }, durationMs);
  }

  dismissToast(): void {
    this.showSuccessToast = false;
    if (this._toastTimer !== null) { clearTimeout(this._toastTimer); this._toastTimer = null; }
  }

  // ---- Clear All -----------------------------------------------------------

  /** Show the Clear All confirmation dialog. */
  openClearConfirm(): void {
    this.showClearConfirm = true;
  }

  /** Dismiss the Clear All dialog without taking action. */
  cancelClearConfirm(): void {
    this.showClearConfirm = false;
  }

  /**
   * Confirmed: reset every form field, clear all files, and dismiss the dialog.
   * The analystName (auto-filled from Entra ID) is preserved.
   */
  confirmClearAll(): void {
    this.showClearConfirm = false;

    // Reset form fields (preserve analystName — it comes from Entra ID)
    const name = this.form.analystName;
    this.form = {
      persona:         '',
      analystName:     name,
      catId:           '',
      appId:           '',
      appName:         '',
      managementStyle: '',
      appTypes:        [],
      tier:            '',
      slLevel:         '',
      archDescription: '',
      additionalInfo:  '',
      linkCount:       0,
      links:           [],
    };

    // Clear files
    this.archFile       = null;
    this.archFileRejected = null;
    this.archFileRejectedReason = null;
    this.artifactFiles  = [];
    this.nonAsciiFiles  = [];
    this.unsupportedFiles = [];
    this.oversizedFiles = [];

    // Clear validation state
    this.touchedFields  = {};
    this.submitError    = null;
    this.linkReduceWarning = false;
    this.appTypeDropdownOpen = false;
    this.isEditMode = false;

    // Show success toast to confirm the action
    this._showToast('✓ All fields cleared successfully.');
  }

  // ---- Back-navigation: load existing session from backend ----------------

  private _loadExistingSession(sessionId: string): void {
    this.isLoadingSession = true;
    this.submitError = null;

    this.uploadService.getSession(sessionId).subscribe({
      next: (meta) => {
        const d = meta.application_details as any;

        // The backend stores fields with snake_case keys (model_dump by_alias=False).
        // Support both snake_case and camelCase so either serialisation works.
        this.form.persona         = d.persona                                   ?? '';
        this.form.analystName     = d.analyst_name     ?? d.analystName         ?? this.form.analystName;
        this.form.catId           = d.cat_id           ?? d.catId               ?? '';
        this.form.appId           = d.app_id           ?? d.appId               ?? '';
        this.form.appName         = d.app_name         ?? d.appName             ?? '';
        this.form.managementStyle = d.management_style ?? d.managementStyle     ?? '';
        this.form.appTypes        = d.app_types        ?? d.appTypes            ?? [];
        this.form.tier            = d.tier                                       ?? '';
        this.form.slLevel         = d.sl_level         ?? d.slLevel             ?? '';
        this.form.archDescription = d.arch_description ?? d.archDescription     ?? '';
        this.form.additionalInfo  = d.additional_info  ?? d.additionalInfo      ?? '';
        this.form.linkCount       = d.link_count       ?? d.linkCount           ?? 0;

        const linkStrings: string[] = d.links ?? [];
        this.form.links = linkStrings.map((v: string) => ({ value: v }));
        this.syncLinksArray();

        // Populate existing files list from backend metadata
        this.artifactFiles = (meta.uploaded_files ?? []).map((f: UploadedFileInfo) => ({
          file:              null,
          displayName:       f.original_filename,
          displaySize:       f.size_bytes,
          status:            'uploaded' as const,
          source:            'existing' as const,
          storedFilename:    f.stored_filename,
          markedForDeletion: false,
        }));

        this.isLoadingSession = false;

        // Take a snapshot of the just-loaded state so we can detect changes.
        this._loadedSnapshot = this._formSnapshot();
      },
      error: (err: Error) => {
        this.isLoadingSession = false;
        this.submitError = `Could not load session data: ${err.message}`;
        this.isEditMode = false;  // fall back to new-session mode
      },
    });
  }

  // ---- Form submission ----------------------------------------------------

  /**
   * Serialise the current form + file list to a stable JSON string.
   * Used to detect whether anything changed since the session was loaded.
   */
  private _formSnapshot(): string {
    return JSON.stringify({
      form:      this.form,
      archFile:  this.archFile ? this.archFile.name : null,
      artifacts: this.artifactFiles.map(e => ({
        name:              e.displayName,
        source:            e.source,
        markedForDeletion: e.markedForDeletion,
      })),
    });
  }

  /**
   * Returns true if the user has NOT changed anything since the session was
   * loaded (edit mode).  When true we skip the re-upload.
   */
  private _isUnchangedSinceLoad(): boolean {
    return this._loadedSnapshot !== '' &&
           this._formSnapshot() === this._loadedSnapshot;
  }

  onSubmit(): void {
    if (!this.isFormReady || this.isSubmitting) { return; }

    // ── Edit-mode fast-path: nothing changed → skip upload entirely ──────
    if (this.isEditMode && this._isUnchangedSinceLoad()) {
      this.router.navigate(['/category-review']);
      return;
    }

    this.isSubmitting = true;
    this.submitError  = null;

    const payload: ApplicationDetailsPayload = {
      persona:         this.form.persona,
      analystName:     this.form.analystName,
      catId:           this.form.catId,
      appId:           this.form.appId,
      appName:         this.form.appName,
      managementStyle: this.form.managementStyle,
      appTypes:        this.form.appTypes,
      tier:            this.form.tier,
      slLevel:         this.form.slLevel || null,
      archDescription: this.form.archDescription,
      additionalInfo:  this.form.additionalInfo,
      linkCount:       this.form.linkCount,
      links:           this.form.links.map(l => l.value).filter(v => v.trim()),
    };

    if (this.isEditMode) {
      this._submitUpdate(payload);
    } else {
      this._submitNew(payload);
    }
  }

  /** Create a brand-new session (first visit). */
  private _submitNew(payload: ApplicationDetailsPayload): void {
    this.artifactFiles.forEach(f => f.status = 'pending');
    this.uploadService
      .uploadSession(payload, this.archFile, this.artifactFiles.map(e => e.file as File))
      .subscribe({
        next: (response) => {
          this.artifactFiles.forEach(f => f.status = 'uploaded');
          this.sessionService.setSessionId(response.session_id);
          this.sessionService.markStepComplete('document-upload');
          this.isSubmitting = false;
          const count = response.uploaded_files.length;

          // Warn about any webpage URLs that could not be converted to PDF
          if (response.webpage_errors?.length) {
            const failedUrls = response.webpage_errors.map(e => e.url).join(', ');
            this.submitError = (
              `⚠ ${response.webpage_errors.length} webpage PDF(s) could not be generated: ${failedUrls}. ` +
              `All other files were uploaded. Click "Upload & Continue" to proceed anyway.`
            );
            this.isSubmitting = false;
            // Still mark uploaded docs and update the list
            this.artifactFiles = response.uploaded_files.map(f => ({
              file:              null,
              displayName:       f.original_filename,
              displaySize:       f.size_bytes,
              status:            'uploaded' as const,
              source:            'existing' as const,
              storedFilename:    f.stored_filename,
              markedForDeletion: false,
            }));
            return;
          }

          this._showToast(`✓ ${count} document${count !== 1 ? 's' : ''} uploaded successfully!`);
          this.router.navigate(['/category-review']);
        },
        error: (err: Error) => {
          this.isSubmitting = false;
          this.submitError  = err.message;
          this.artifactFiles.forEach(f => { if (f.source === 'new') { f.status = 'pending'; } });
        },
      });
  }

  /** Update the existing session (back-navigation edit). */
  private _submitUpdate(payload: ApplicationDetailsPayload): void {
    const sessionId = this.sessionService.sessionId()!;

    // Stored filenames to delete from blob storage
    const filesToDelete: string[] = this.artifactFiles
      .filter(e => e.source === 'existing' && e.markedForDeletion && e.storedFilename)
      .map(e => e.storedFilename as string);

    // New files to upload
    const newArtifacts: File[] = this.artifactFiles
      .filter(e => e.source === 'new' && e.file)
      .map(e => e.file as File);

    this.uploadService
      .updateSession(sessionId, payload, filesToDelete, this.archFile, newArtifacts)
      .subscribe({
        next: (response) => {
          // Replace local list with the authoritative list returned by the backend
          this.artifactFiles = response.uploaded_files.map(f => ({
            file:              null,
            displayName:       f.original_filename,
            displaySize:       f.size_bytes,
            status:            'uploaded' as const,
            source:            'existing' as const,
            storedFilename:    f.stored_filename,
            markedForDeletion: false,
          }));
          this.archFile = null;
          this.sessionService.markStepComplete('document-upload');
          this.isSubmitting = false;

          const added   = newArtifacts.length;
          const removed = filesToDelete.length;
          const parts: string[] = [];
          if (added   > 0) { parts.push(`${added} file${added !== 1 ? 's' : ''} added`); }
          if (removed > 0) { parts.push(`${removed} file${removed !== 1 ? 's' : ''} removed`); }
          const detail = parts.length > 0 ? ` (${parts.join(', ')})` : '';

          // Warn about any webpage URLs that could not be converted to PDF
          if (response.webpage_errors?.length) {
            const failedUrls = response.webpage_errors.map(e => e.url).join(', ');
            this.submitError = (
              `⚠ ${response.webpage_errors.length} webpage PDF(s) could not be generated: ${failedUrls}. ` +
              `All other changes were saved.`
            );
            return;
          }

          this._showToast(`✓ Session updated successfully${detail}!`);
          this.router.navigate(['/category-review']);
        },
        error: (err: Error) => {
          this.isSubmitting = false;
          this.submitError  = err.message;
        },
      });
  }
}